import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProjects, getPlots, getStats } from '../utils/api';
import { generateProjectPDF, generateAllProjectsPDF, generateWhatsAppMessage } from '../utils/pdf';
import { FileText, Share2, MapPin, Monitor } from 'lucide-react';

const STATUS_LABELS = {
  available: 'Available',
  booked: 'Booked',
  hold: 'On Hold',
  nfs: 'Not For Sale',
};

function Header() {
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <header style={{
      background: 'linear-gradient(135deg, #1D9E75 0%, #0F6E56 100%)',
      color: '#fff', padding: '0 28px', height: 68,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 200,
      boxShadow: '0 2px 16px rgba(0,0,0,0.18)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 42, height: 42, background: 'rgba(255,255,255,0.18)',
          borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13, border: '1.5px solid rgba(255,255,255,0.3)'
        }}>SRG</div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>SRG Real Estate Pvt. Ltd.</div>
          <div style={{ fontSize: 11.5, opacity: 0.82, marginTop: 1 }}>Plot Inventory Dashboard — Jaipur</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, background: 'rgba(255,255,255,0.12)', padding: '5px 12px', borderRadius: 20 }}>
          📞 9166523869 | 9166523778
        </div>
        <div style={{ fontSize: 12, background: 'rgba(255,255,255,0.15)', padding: '5px 14px', borderRadius: 20 }}>
          {today}
        </div>
        <a href="/admin" style={{
          fontSize: 12, background: 'rgba(255,255,255,0.2)', padding: '6px 14px',
          borderRadius: 20, color: '#fff', textDecoration: 'none', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.3)'
        }}>
          <Monitor size={13} /> Admin
        </a>
      </div>
    </header>
  );
}

function MapModal({ project, onClose }) {
  if (!project) return null;
  const gMaps = `https://www.google.com/maps/search/?api=1&query=${project.map_query || `${project.name}+Jaipur+Rajasthan`}`;
  const appMaps = `https://maps.apple.com/?q=${encodeURIComponent(project.name + ' Jaipur Rajasthan')}`;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-head">
          <h3>📍 {project.name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <a href={gMaps} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: 14,
              borderRadius: 10, border: '1.5px solid var(--border)', cursor: 'pointer', marginBottom: 10,
              transition: 'all 0.15s'
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.background = 'var(--green-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = ''; }}
            >
              <div style={{ width: 42, height: 42, background: '#E8F5E9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🗺️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Google Maps</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Search on Google Maps</div>
              </div>
              <span style={{ color: 'var(--green)' }}>→</span>
            </div>
          </a>
          <a href={appMaps} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: 14,
              borderRadius: 10, border: '1.5px solid var(--border)', cursor: 'pointer',
              transition: 'all 0.15s'
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.background = 'var(--green-light)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = ''; }}
            >
              <div style={{ width: 42, height: 42, background: '#E3F2FD', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🍎</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Apple Maps</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Turn-by-turn directions</div>
              </div>
              <span style={{ color: 'var(--green)' }}>→</span>
            </div>
          </a>
          <p style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', marginTop: 6 }}>
            ⚠️ Hold sirf 24 ghante ke liye hoga, uske baad release ho jaayega.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [activeProjectIdx, setActiveProjectIdx] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [mapProject, setMapProject] = useState(null);

  const { data: projects = [], isLoading } = useQuery({ queryKey: ['projects'], queryFn: getProjects });
  const { data: globalStats } = useQuery({ queryKey: ['stats'], queryFn: getStats });

  const activeProject = projects[activeProjectIdx];

  const { data: plots = [] } = useQuery({
    queryKey: ['plots', activeProject?.id],
    queryFn: () => getPlots({ project_id: activeProject.id }),
    enabled: !!activeProject?.id,
  });

  const filteredPlots = statusFilter === 'all' ? plots : plots.filter(p => p.status === statusFilter);

  const handleWAShare = () => {
    if (!activeProject) return;
    const msg = generateWhatsAppMessage(activeProject, plots);
    window.open('https://api.whatsapp.com/send?text=' + encodeURIComponent(msg), '_blank');
  };

  if (isLoading) return (
    <>
      <Header />
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
    </>
  );

  return (
    <>
      <Header />
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '24px 20px 40px' }}>

        {/* Global Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          {[
            { label: 'Total Plots', val: globalStats?.grand_total || '—', color: 'var(--blue)', w: '100%' },
            { label: 'Available', val: globalStats?.total_available || '—', color: 'var(--green)', w: `${(globalStats?.total_available / globalStats?.grand_total) * 100}%` },
            { label: 'Booked', val: globalStats?.total_booked || '—', color: 'var(--red)', w: `${(globalStats?.total_booked / globalStats?.grand_total) * 100}%` },
            { label: 'Not For Sale', val: globalStats?.total_nfs || '—', color: 'var(--gray)', w: `${(globalStats?.total_nfs / globalStats?.grand_total) * 100}%` },
          ].map(({ label, val, color, w }) => (
            <div key={label} className="stat-card">
              <div className="stat-val" style={{ color }}>{val}</div>
              <div className="stat-lbl">{label}</div>
              <div className="stat-bar"><div className="stat-bar-fill" style={{ width: w, background: color }} /></div>
            </div>
          ))}
        </div>

        {/* Action Bar */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 22 }}>
          <button className="btn btn-green" onClick={() => generateProjectPDF(activeProject, plots)} disabled={!activeProject}>
            <FileText size={15} /> PDF Generate Karo
          </button>
          <button className="btn btn-wa" onClick={handleWAShare} disabled={!activeProject}>
            <Share2 size={15} /> WhatsApp Share
          </button>
          <button className="btn btn-blue" onClick={() => generateAllProjectsPDF(projects)} disabled={!projects.length}>
            <FileText size={15} /> Sabhi Projects PDF
          </button>
          <button className="btn btn-outline" onClick={() => setMapProject(activeProject)} disabled={!activeProject}>
            <MapPin size={15} /> Project Map
          </button>
        </div>

        {/* Project Tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, overflowX: 'auto' }}>
          {projects.map((proj, i) => (
            <button
              key={proj.id}
              onClick={() => { setActiveProjectIdx(i); setStatusFilter('all'); }}
              style={{
                padding: '8px 18px', borderRadius: 20, border: '1.5px solid',
                fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                transition: 'all 0.15s', whiteSpace: 'nowrap',
                background: i === activeProjectIdx ? 'var(--green)' : 'var(--white)',
                color: i === activeProjectIdx ? '#fff' : 'var(--text2)',
                borderColor: i === activeProjectIdx ? 'var(--green)' : 'var(--border)',
                boxShadow: i === activeProjectIdx ? '0 2px 8px rgba(29,158,117,0.3)' : 'none',
              }}
            >
              {proj.name}
            </button>
          ))}
        </div>

        {/* Project Panel */}
        {activeProject && (
          <div className="card" style={{ overflow: 'hidden' }}>
            {/* Panel Header */}
            <div className="card-header" style={{ background: '#f8fffe' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="card-title">{activeProject.name}</span>
                <span style={{ fontSize: 12, background: 'var(--green-light)', color: 'var(--green-dark)', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>
                  {activeProject.type}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { label: `${activeProject.available} Available`, cls: 'badge-available' },
                  { label: `${activeProject.booked} Booked`, cls: 'badge-booked' },
                  { label: `${activeProject.nfs} NFS`, cls: 'badge-nfs' },
                  { label: `${activeProject.total} Total`, cls: 'badge-hold' },
                ].map(b => (
                  <span key={b.label} className={`badge ${b.cls}`}>{b.label}</span>
                ))}
              </div>
            </div>

            {/* Progress */}
            <div style={{ padding: '12px 20px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                <span>Booking Progress</span>
                <span>{activeProject.total > 0 ? Math.round((activeProject.booked / activeProject.total) * 100) : 0}% Booked</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill" style={{ width: `${activeProject.total > 0 ? (activeProject.booked / activeProject.total) * 100 : 0}%` }} />
              </div>
            </div>

            {/* Filter Bar */}
            <div className="filter-bar">
              <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>Filter:</span>
              {['all', 'available', 'booked', 'hold', 'nfs'].map(s => (
                <button key={s} className={`fchip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? `All (${plots.length})` : `${STATUS_LABELS[s]} (${plots.filter(p => p.status === s).length})`}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Plot No.</th>
                    <th>Facing</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlots.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text3)' }}>
                      No plots match this filter
                    </td></tr>
                  ) : filteredPlots.map((plot, i) => (
                    <tr key={plot.id}>
                      <td className="mono">{i + 1}</td>
                      <td style={{ fontWeight: 700, fontSize: 13.5 }}>{plot.plot_no}</td>
                      <td>{plot.facing || '—'}</td>
                      <td className="mono">{plot.size || '—'}</td>
                      <td>
                        <span className={`badge badge-${plot.status}`}>
                          {STATUS_LABELS[plot.status] || plot.status}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text2)' }}>{plot.booked_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <footer style={{ textAlign: 'center', padding: 24, color: 'var(--text3)', fontSize: 12, borderTop: '1px solid var(--border)', background: 'var(--white)', marginTop: 10 }}>
        <strong style={{ color: 'var(--green)' }}>SRG Real Estate Pvt. Ltd.</strong> &nbsp;|&nbsp;
        Plot No. 1, Gangaram Tower, Ganesh Nagar, Muhana Mandi Road, Gate No.1, Jaipur &nbsp;|&nbsp;
        📞 9166523869 &nbsp;|&nbsp; 📞 9166523778
      </footer>

      {mapProject && <MapModal project={mapProject} onClose={() => setMapProject(null)} />}
    </>
  );
}
